import React from 'react';
import { Clock, FileText, Factory, Cog } from 'lucide-react';
import { GlobalSearchData, SearchRfqHit, SearchSupplierHit, SearchPartHit } from '../../client/searchClient';
import { displayRfqId } from '../../utils/rfqIdentity';

/** One keyboard-navigable row of the dropdown, across all groups. */
export type SearchResultItem =
  | { kind: 'rfq'; hit: SearchRfqHit }
  | { kind: 'supplier'; hit: SearchSupplierHit }
  | { kind: 'part'; hit: SearchPartHit }
  | { kind: 'recent'; value: string };

/** Flat row list in render order — the container's arrow keys walk this. */
export const flattenResults = (data: GlobalSearchData | null, recents: string[], showRecents: boolean): SearchResultItem[] => {
  if (showRecents) return recents.map((value) => ({ kind: 'recent', value }));
  if (!data) return [];
  return [
    ...data.rfqs.map((hit): SearchResultItem => ({ kind: 'rfq', hit })),
    ...data.suppliers.map((hit): SearchResultItem => ({ kind: 'supplier', hit })),
    ...data.parts.map((hit): SearchResultItem => ({ kind: 'part', hit })),
  ];
};

const formatStatus = (status: string) => status.replace(/_/g, ' ').toLowerCase();

interface SearchResultsProps {
  query: string;
  data: GlobalSearchData | null;
  loading: boolean;
  error: boolean;
  items: SearchResultItem[];
  showRecents: boolean;
  activeIndex: number;
  onSelect: (item: SearchResultItem) => void;
  onHover: (index: number) => void;
}

const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-s5 pb-s1 pt-s4 text-table-header uppercase text-ink-muted">{children}</div>
);

const StateRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-s5 py-s4 text-body-md text-ink-muted">{children}</div>
);

/**
 * Grouped dropdown under the top-bar search — RFQs / Suppliers / Parts (the
 * mocks show only the input, so this stays minimal in the v2 language: white
 * panel, 1px line borders, floating shadow, cobalt highlight for the active
 * row). Presentational; the container owns fetching, keyboard state and
 * navigation.
 */
export const SearchResults: React.FC<SearchResultsProps> = ({
  query, data, loading, error, items, showRecents, activeIndex, onSelect, onHover,
}) => {
  let index = -1;
  const row = (item: SearchResultItem, icon: React.ReactNode, title: React.ReactNode, meta: React.ReactNode) => {
    index += 1;
    const i = index;
    return (
      <button
        key={`${item.kind}-${i}`}
        type="button"
        role="option"
        id={`global-search-option-${i}`}
        aria-selected={i === activeIndex}
        onMouseEnter={() => onHover(i)}
        onMouseDown={(e) => e.preventDefault() /* keep input focus */}
        onClick={() => onSelect(item)}
        className={`flex w-full items-center gap-s3 px-s5 py-[7px] text-left ${i === activeIndex ? 'bg-action-bg' : 'hover:bg-surface-hover'}`}
      >
        <span className="shrink-0 text-ink-muted">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-row-title text-ink">{title}</span>
          {meta && <span className="block truncate text-meta text-ink-muted">{meta}</span>}
        </span>
      </button>
    );
  };

  const empty = !loading && !error && !showRecents && data &&
    data.rfqs.length === 0 && data.suppliers.length === 0 && data.parts.length === 0;

  return (
    <div
      role="listbox"
      id="global-search-results"
      aria-label="Search results"
      className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[420px] overflow-y-auto rounded-panel border border-line bg-surface pb-s3 shadow-floating"
    >
      {loading && <StateRow>Searching…</StateRow>}
      {error && !loading && <StateRow>Search isn’t responding right now — try again.</StateRow>}
      {empty && <StateRow>No RFQs, suppliers or parts match “{query}”.</StateRow>}

      {showRecents && items.length > 0 && (
        <>
          <GroupLabel>Recent searches</GroupLabel>
          {items.map((item) => item.kind === 'recent' &&
            row(item, <Clock className="h-[13px] w-[13px]" />, item.value, null))}
        </>
      )}

      {!showRecents && !loading && !error && data && (
        <>
          {data.rfqs.length > 0 && (
            <>
              <GroupLabel>RFQs</GroupLabel>
              {data.rfqs.map((hit) => row(
                { kind: 'rfq', hit },
                <FileText className="h-[13px] w-[13px]" />,
                <>
                  <span className="font-mono text-mono-data text-ink-secondary">{displayRfqId(hit.rfqId)}</span>
                  <span className="ml-s3">{hit.name}</span>
                </>,
                formatStatus(hit.status),
              ))}
              {data.truncated.rfqs && <StateRow>More RFQs match — keep typing to narrow down.</StateRow>}
            </>
          )}
          {data.suppliers.length > 0 && (
            <>
              <GroupLabel>Suppliers</GroupLabel>
              {data.suppliers.map((hit) => row(
                { kind: 'supplier', hit },
                <Factory className="h-[13px] w-[13px]" />,
                hit.name,
                <>on <span className="font-mono">{displayRfqId(hit.rfqId)}</span> · {hit.componentName}</>,
              ))}
              {data.truncated.suppliers && <StateRow>More suppliers match — keep typing to narrow down.</StateRow>}
            </>
          )}
          {data.parts.length > 0 && (
            <>
              <GroupLabel>Parts</GroupLabel>
              {data.parts.map((hit) => row(
                { kind: 'part', hit },
                <Cog className="h-[13px] w-[13px]" />,
                hit.name,
                <>on <span className="font-mono">{displayRfqId(hit.rfqId)}</span>{hit.manufacturer ? ` · ${hit.manufacturer}` : ''}</>,
              ))}
              {data.truncated.parts && <StateRow>More parts match — keep typing to narrow down.</StateRow>}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SearchResults;
